import React, { forwardRef } from 'react';
import { SelectControl as LegacySelectControl } from './component.legacy';
import {
  SelectControl as NewSelectControl,
  SelectControlHandle,
  SelectControlProps,
  SelectControlOption,
  SelectControlSingleProps,
  SelectControlMultiProps,
} from './component';
import { OptionBase } from '../../../../core/components/Combobox/types';

export type { SelectControlHandle, SelectControlProps } from './component';

type NullableArray<T> = readonly T[] | T[] | null | undefined;

interface SelectControlBridgeProps<T extends OptionBase = SelectControlOption> extends SelectControlProps<T> {
  renderAsLegacy?: boolean;
}
type SelectControlBridgeSingleProps<T extends OptionBase> = SelectControlSingleProps<T> & { renderAsLegacy?: boolean };

type SelectControlBridgeMultiProps<T extends OptionBase> = SelectControlMultiProps<T> & { renderAsLegacy?: boolean };

type SelectControlComponent = {
  <T extends OptionBase = SelectControlOption>(
    props: SelectControlBridgeSingleProps<T> & { ref?: React.Ref<SelectControlHandle> }
  ): React.ReactElement | null;
  <T extends OptionBase = SelectControlOption>(
    props: SelectControlBridgeMultiProps<T> & { ref?: React.Ref<SelectControlHandle> }
  ): React.ReactElement | null;
};

export const SelectControl = forwardRef(
  <T extends OptionBase = SelectControlOption>(
    { renderAsLegacy = true, ...restProps }: SelectControlBridgeProps<T>,
    ref: React.Ref<SelectControlHandle>
  ) => {
    if (renderAsLegacy) {
      const Component = LegacySelectControl as React.ComponentType<
        SelectControlBridgeProps<T> & { ref?: React.Ref<SelectControlHandle> }
      >;
      return <Component {...restProps} ref={ref} />;
    }

    // Runtime guard to resolve overloads safely
    const isMultiEnabled = restProps.isMulti === true || restProps.multi === true;
    if (isMultiEnabled) {
      return <NewSelectControl<T> ref={ref} {...(restProps as SelectControlMultiProps<T>)} />;
    }
    return <NewSelectControl<T> ref={ref} {...(restProps as SelectControlSingleProps<T>)} />;
  }
) as SelectControlComponent;
(SelectControl as { displayName?: string }).displayName = 'SelectControl';
